﻿import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { FeatureKey } from '../constants/featureKeys';

/**
* Hook to check feature availability and limits for the current company
* Features are loaded from the backend which handles beta access, overrides, and subscription rules.
* 
* @example
* const { enabled, limit, isUnlimited } = useFeature(FeatureKeys.MaxActiveEvents);
* if (!enabled) return <UpgradePrompt />;
* return <div>Active Events: {count} / {isUnlimited ? '∞' : limit}</div>;
*/
export const useFeature = (featureKey: FeatureKey) => {
    const company = useSelector((state: RootState) => state.lookup.company);

    if (!company || !company.features) {
        return {
            enabled: false,
            limit: 0,
            isUnlimited: false,
            isLoading: true,
            feature: null,
        };
    }

    const feature = company.features[featureKey];

    if (!feature) {
        return {
            enabled: false,
            limit: 0,
            isUnlimited: false,
            isLoading: false,
            feature: null,
        };
    }

    return {
        enabled: feature.isEnabled,
        limit: feature.limit,
        isUnlimited: (feature.limit == null) && feature.isEnabled, // == null catches both null and undefined
        isLoading: false,
        feature: feature,
    };
};

/**
 * Hook to check if a feature limit has been reached
 * 
 * @param featureKey - The feature to check
 * @param currentCount - The current usage count
 * @returns Object with isLimitReached, limit, and usage info
 * 
 * @example
 * const { isLimitReached, limit, usage } = useFeatureLimit(FeatureKeys.MaxActiveEvents, activeEventsCount);
 * if (isLimitReached) return <UpgradePrompt />;
 */
export const useFeatureLimit = (featureKey: FeatureKey, currentCount: number) => {
    const featureInfo = useFeature(featureKey);

    // If feature is not enabled or unlimited, handle accordingly
    if (!featureInfo.enabled || featureInfo.isUnlimited) {
        return {
            isLimitReached: false,
            limit: featureInfo.limit,
            usage: currentCount,
            remaining: featureInfo.isUnlimited ? Infinity : 0,
        };
    }

    const isLimitReached = currentCount >= (featureInfo.limit || 0);

    return {
        isLimitReached,
        limit: featureInfo.limit || 0,
        usage: currentCount,
        remaining: Math.max(0, (featureInfo.limit || 0) - currentCount),
    };
};

/**
 * Hook to get all features for the current company
 */
export const useFeatures = () => {
    const company = useSelector((state: RootState) => state.lookup.company);

    return {
        features: company?.features ?? {},
        subscriptionName: company?.subscriptionName ?? 'Free',
        isBetaEnabled: company?.isBetaEnabled ?? false,
        isCharityEnabled: company?.isCharityEnabled ?? false,
        isLoading: !company,
    };
};

/**
 * Hook to check if a feature is available (simple boolean check)
 * 
 * @example
 * const canUsePaidTickets = useHasFeature(FeatureKeys.PaidTicketsEnabled);
 * if (!canUsePaidTickets) return <Chip label="Pro Feature" />;
 */
export const useHasFeature = (featureKey: FeatureKey): boolean => {
    const { enabled } = useFeature(featureKey);
    return enabled;
};
